import { useLocation, useRoute } from 'wouter';
import useSWR from 'swr';
import { ArrowLeft, FileText, Package, Clock, AlertCircle, Percent, Timer, Truck } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { useAuthStore } from '../hooks/useAuth';
import { formatDateInTimeZone } from '../utils/timezoneDisplay';

interface PurchaseOrder {
    id: string;
    poNumber: string;
    status: string;
    supplier: string;
    expectedDate: string | null;
    createdAt: string;
}

interface Appointment {
    id: string;
    customerPoNumber: string;
    status: string;
    startAt: string;
    carrierName: string | null;
    dockId: string | null;
    dwellMinutes: number | null;
    wasLate: boolean;
}

interface CustomerStats {
    poCount: number;
    appointmentCount: number;
    completedLoadsCount: number;
    onTimePct: number;
    avgDwellMinutes: number;
}

interface HistoryResponse {
    pos: PurchaseOrder[];
    appointments: Appointment[];
    stats: CustomerStats;
}

// Inline Stat Card Component for simplicity if generic one is not suitable
const SimpleStatCard = ({ title, value, icon: Icon, subtext, color = "primary" }: any) => (
    <div className="bg-card border border-border rounded-xl p-6 shadow-sm">
        <div className="flex items-center justify-between pb-2">
            <h3 className="text-sm font-medium text-muted-foreground">{title}</h3>
            <Icon className={`h-4 w-4 text-${color}`} />
        </div>
        <div className="text-2xl font-bold text-foreground">{value}</div>
        {subtext && <p className="text-xs text-muted-foreground mt-1">{subtext}</p>}
    </div>
);

export function CustomerHistory() {
    const { user } = useAuthStore();
    const [, setLocation] = useLocation();
    const [match, params] = useRoute('/customers/:id/history');
    const customerId = match ? params?.id : null;

    const { data, error, isLoading } = useSWR<HistoryResponse>(
        customerId ? `/api/customers/${customerId}/history` : null
    );

    if (!customerId) {
        return <div className="p-8 text-center text-red-500">Invalid Customer ID</div>;
    }

    return (
        <div className="p-6 max-w-[1600px] mx-auto flex flex-col min-h-0 h-full">
            {/* Header */}
            <div className="flex items-center gap-4 mb-8">
                <button
                    onClick={() => setLocation('/customers')}
                    className="h-10 w-10 flex items-center justify-center rounded-lg hover:bg-muted/50 transition-colors border border-transparent hover:border-border"
                    aria-label="Go back"
                >
                    <ArrowLeft className="h-5 w-5 text-foreground" />
                </button>
                <div>
                    <h1 className="text-2xl font-bold font-outfit text-foreground">Customer 360</h1>
                    <p className="text-muted-foreground text-sm">Comprehensive view of activity and performance</p>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 min-h-0 overflow-y-auto space-y-8 pb-10">

                {isLoading ? (
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-32 bg-muted/20 animate-pulse rounded-xl" />
                        ))}
                    </div>
                ) : error ? (
                    <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                        <AlertCircle className="h-8 w-8 mb-2 text-destructive" />
                        <p>Failed to load customer data</p>
                    </div>
                ) : (
                    <>
                        {/* Stats Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                            <SimpleStatCard
                                title="Total POs"
                                value={data?.stats?.poCount || 0}
                                icon={FileText}
                                subtext="All time volume"
                            />
                            <SimpleStatCard
                                title="On-Time Rate"
                                value={`${data?.stats?.onTimePct?.toFixed(0) || 0}%`}
                                icon={Percent}
                                color="green-500"
                                subtext="Based on completed loads"
                            />
                            <SimpleStatCard
                                title="Avg Dwell Time"
                                value={`${data?.stats?.avgDwellMinutes || 0} min`}
                                icon={Timer}
                                color="orange-500"
                                subtext="Gate-in to Gate-out"
                            />
                            <SimpleStatCard
                                title="Total Appointments"
                                value={data?.stats?.appointmentCount || 0}
                                icon={Truck}
                                subtext="Scheduled & Completed"
                            />
                        </div>

                        {/* Recent Appointments */}
                        <div className="rounded-xl border border-border bg-card text-card-foreground shadow-sm">
                            <div className="flex flex-col space-y-1.5 p-6 border-b border-border">
                                <h3 className="text-lg font-medium flex items-center gap-2">
                                    <Truck className="h-5 w-5 text-primary" />
                                    Recent Appointments
                                </h3>
                            </div>
                            <div className="p-6">
                                {!data?.appointments || data.appointments.length === 0 ? (
                                    <div className="text-center py-8 text-muted-foreground">No recent appointments found.</div>
                                ) : (
                                    <div className="space-y-4">
                                        {data.appointments.map((apt) => (
                                            <div
                                                key={apt.id}
                                                className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className={`p-2 rounded-lg ${apt.wasLate ? 'bg-red-500/10' : 'bg-green-500/10'}`}>
                                                        <Clock className={`h-5 w-5 ${apt.wasLate ? 'text-red-500' : 'text-green-500'}`} />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-foreground">
                                                            {formatDateInTimeZone(apt.startAt, user?.timeZone, true)}
                                                        </div>
                                                        <div className="text-sm text-muted-foreground mt-1">
                                                            PO: <span className="font-mono text-primary">{apt.customerPoNumber || 'N/A'}</span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6">
                                                    <div className="text-right hidden sm:block">
                                                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Carrier</div>
                                                        <div className="text-sm font-medium text-foreground">{apt.carrierName || 'Unknown'}</div>
                                                    </div>
                                                    <Badge variant={
                                                        apt.status === 'completed' ? 'success' :
                                                            apt.status === 'checked_in' ? 'info' :
                                                                apt.status === 'pending' ? 'warning' :
                                                                    'neutral'
                                                    }>
                                                        {apt.status.replace('_', ' ').toUpperCase()}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Purchase Orders */}
                        <div className="rounded-xl border border-border bg-card text-card-foreground shadow-sm">
                            <div className="flex flex-col space-y-1.5 p-6 border-b border-border">
                                <h3 className="text-lg font-medium flex items-center gap-2">
                                    <FileText className="h-5 w-5 text-primary" />
                                    Recent Purchase Orders
                                </h3>
                            </div>
                            <div className="p-6">
                                {!data?.pos || data.pos.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground bg-muted/20 rounded-lg border border-dashed">
                                        <Package className="h-8 w-8 mb-2 opacity-50" />
                                        <p>No purchase history found for this customer.</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {data.pos.map((po) => (
                                            <div
                                                key={po.id}
                                                className="flex items-center justify-between p-4 rounded-lg border border-border bg-card hover:bg-muted/30 transition-colors"
                                            >
                                                <div className="flex items-center gap-4">
                                                    <div className="p-2 bg-primary/10 rounded-lg">
                                                        <Package className="h-5 w-5 text-primary" />
                                                    </div>
                                                    <div>
                                                        <div className="font-medium text-foreground">{po.poNumber}</div>
                                                        <div className="text-sm text-muted-foreground flex items-center gap-4 mt-1">
                                                            <span className="flex items-center gap-1.5">
                                                                <Clock size={14} />
                                                                Created {new Date(po.createdAt).toLocaleDateString()}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-6">
                                                    <div className="text-right">
                                                        <div className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Supplier</div>
                                                        <div className="text-sm font-medium text-foreground">{po.supplier}</div>
                                                    </div>
                                                    <Badge variant={
                                                        po.status === 'completed' ? 'success' :
                                                            po.status === 'in_progress' ? 'info' :
                                                                'neutral'
                                                    }>
                                                        {po.status.replace('_', ' ').toUpperCase()}
                                                    </Badge>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
